/**
 * Tenant utility functions for subdomain detection and tenant context
 */

/**
 * Extract subdomain from current window location
 * @returns The subdomain (e.g., "tfwc" from "tfwc.clnicl.ie") or null if on root domain
 */
export function getCurrentSubdomain(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const hostname = window.location.hostname
  const parts = hostname.split('.')

  // If we have at least 3 parts (subdomain.domain.tld) or 2 parts in localhost
  if (parts.length >= 3 || (parts.length === 2 && parts[0] !== 'www')) {
    const subdomain = parts[0]
    // Don't treat 'www' as a subdomain
    if (subdomain === 'www') {
      return null
    }
    return subdomain
  }

  return null
}

/**
 * Check if currently on the root domain
 * @returns true if on root domain (clnicl.ie), false if on subdomain
 */
export function isRootDomain(): boolean {
  return getCurrentSubdomain() === null
}

/**
 * Get tenant identifier (subdomain)
 * @returns The tenant subdomain or null (excludes environment prefixes)
 */
export function getTenantId(): string | null {
  const { tenant } = getEnvironmentAndTenant()
  return tenant
}

/**
 * Get full tenant domain
 * @param subdomain The subdomain
 * @param rootDomain The root domain (defaults to clnicl.ie)
 * @returns Full domain string (e.g., "tfwc.clnicl.ie")
 */
export function getTenantDomain(subdomain: string, rootDomain: string = 'clnicl.ie'): string {
  return `${subdomain}.${rootDomain}`
}

/**
 * Check if URL is an API Gateway URL (not domain-based)
 */
function isApiGatewayUrl(url: string): boolean {
  return url.includes('execute-api.') || url.includes('.amazonaws.com')
}

/**
 * Get API base URL for current tenant
 * @param apiBaseUrl Base API URL pattern (can include {subdomain} placeholder)
 * @returns The API URL with subdomain substituted
 */
export function getTenantApiUrl(apiBaseUrl?: string): string {
  const { environment, tenant } = getEnvironmentAndTenant()
  
  // If on root domain (no environment, no tenant), use the configured API URL
  if (!environment && !tenant) {
    return apiBaseUrl || (typeof process !== 'undefined' && process.env?.VITE_API_URL) || 'https://api.clnicl.ie'
  }

  // If we have an environment prefix but no tenant (e.g., staging.clnicl.ie)
  // Use the VITE_API_URL that was set at build time for that environment
  // This should already be configured correctly (e.g., api-staging.clnicl.ie or API Gateway URL)
  if (environment && !tenant) {
    // Prefer the passed apiBaseUrl (which should be VITE_API_URL from build)
    if (apiBaseUrl) {
      // If it's an API Gateway URL, use it as-is (don't try to construct domain-based URL)
      if (isApiGatewayUrl(apiBaseUrl)) {
        return apiBaseUrl
      }
      return apiBaseUrl
    }
    // Fallback: try to get from environment variable
    const envApiUrl = typeof process !== 'undefined' && process.env?.VITE_API_URL
    if (envApiUrl) {
      return envApiUrl
    }
    // Last resort: construct from environment (may not match actual API URL)
    const rootDomain = typeof window !== 'undefined' 
      ? window.location.hostname.split('.').slice(-2).join('.')
      : 'clnicl.ie'
    return `https://api-${environment}.${rootDomain}`
  }

  // If we have a tenant (with or without environment prefix)
  // Replace {subdomain} placeholder if present
  if (apiBaseUrl && apiBaseUrl.includes('{subdomain}')) {
    return apiBaseUrl.replace('{subdomain}', tenant!)
  }

  // If apiBaseUrl is an API Gateway URL, we can't construct tenant-specific domain URLs from it
  // In this case, the API Gateway should handle tenant routing via X-Tenant-ID header
  if (apiBaseUrl && isApiGatewayUrl(apiBaseUrl)) {
    return apiBaseUrl
  }

  // Construct tenant-specific API URL
  // Pattern: api.{tenant}.{rootDomain} for production
  // Pattern: api.{environment}.{tenant}.{rootDomain} for non-production
  const rootDomain = typeof window !== 'undefined'
    ? window.location.hostname.split('.').slice(-2).join('.')
    : 'clnicl.ie'
  
  if (environment && environment !== 'production' && tenant) {
    // Non-production with tenant: api.staging.tenant.clnicl.ie
    return `https://api.${environment}.${tenant}.${rootDomain}`
  } else if (tenant) {
    // Production with tenant: api.tenant.clnicl.ie
    return `https://api.${tenant}.${rootDomain}`
  }

  // Fallback
  return apiBaseUrl || 'https://api.clnicl.ie'
}

/**
 * Validate subdomain format
 * @param subdomain The subdomain to validate
 * @returns true if valid, false otherwise
 */
export function isValidSubdomain(subdomain: string): boolean {
  // Must be 3-63 characters, lowercase alphanumeric with hyphens only
  // Cannot start or end with hyphen
  const subdomainRegex = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/
  return subdomainRegex.test(subdomain)
}

/**
 * Reserved subdomains that cannot be used
 */
export const RESERVED_SUBDOMAINS = [
  'www',
  'api',
  'admin',
  'app',
  'staging',
  'dev',
  'test',
  'mail',
  'ftp',
  'localhost',
  'blog',
  'support',
  'help',
  'docs',
  'status',
]

/**
 * Environment prefixes that indicate deployment environment
 */
export const ENVIRONMENT_PREFIXES = [
  'staging',
  'app',
  'prod',
  'production',
  'dev',
  'development',
  'test',
]

/**
 * Check if subdomain is reserved
 * @param subdomain The subdomain to check
 * @returns true if reserved, false otherwise
 */
export function isReservedSubdomain(subdomain: string): boolean {
  return RESERVED_SUBDOMAINS.includes(subdomain.toLowerCase())
}

/**
 * Check if currently on admin domain
 * @returns true if on admin subdomain (admin.clnicl.ie or staging.admin.clnicl.ie)
 */
export function isAdminDomain(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const hostname = window.location.hostname
  return hostname.includes('admin.')
}

/**
 * Check if subdomain is an environment prefix
 * @param subdomain The subdomain to check
 * @returns true if it's an environment prefix, false otherwise
 */
export function isEnvironmentPrefix(subdomain: string): boolean {
  return ENVIRONMENT_PREFIXES.includes(subdomain.toLowerCase())
}

/**
 * Extract environment and tenant from hostname
 * Handles patterns like:
 * - staging.clnicl.ie -> { environment: 'staging', tenant: null }
 * - app.clnicl.ie -> { environment: 'production', tenant: null }
 * - staging.tfwc.clnicl.ie -> { environment: 'staging', tenant: 'tfwc' }
 * - tfwc.clnicl.ie -> { environment: 'production', tenant: 'tfwc' }
 * @returns Object with environment and tenant, or null values
 */
export function getEnvironmentAndTenant(): { environment: string | null; tenant: string | null } {
  if (typeof window === 'undefined') {
    return { environment: null, tenant: null }
  }

  const hostname = window.location.hostname
  const parts = hostname.split('.')

  // Need at least domain.tld
  if (parts.length < 2) {
    return { environment: null, tenant: null }
  }

  // Root domain (clnicl.ie)
  if (parts.length === 2) {
    return { environment: null, tenant: null }
  }

  const firstPart = parts[0]
  const secondPart = parts.length >= 4 ? parts[1] : null

  // Check if first part is an environment prefix
  if (isEnvironmentPrefix(firstPart)) {
    const environment = firstPart === 'app' || firstPart === 'prod' || firstPart === 'production' 
      ? 'production' 
      : firstPart
    
    // If there's a second part, it's the tenant
    if (secondPart && !isEnvironmentPrefix(secondPart) && !isReservedSubdomain(secondPart)) {
      return { environment, tenant: secondPart }
    }
    
    // Otherwise, just environment prefix (e.g., staging.clnicl.ie)
    return { environment, tenant: null }
  }

  // First part is not an environment prefix, so it's a tenant in production
  if (!isReservedSubdomain(firstPart)) {
    return { environment: 'production', tenant: firstPart }
  }

  return { environment: null, tenant: null }
}








